import {
  SBSelectRaw,
  SBState,
  baseReducers,
  getIdOrModelId,
} from '../utils/helpers/ReducerHelper'
import {
  SBAPICreateDispatch,
  SBAPIDeleteDispatch,
  SBAPIFetchDispatch,
  SBAPIFetchPaginatedDispatch,
  SBAPIUpdateDispatch,
} from '../utils/helpers/SBAPIHelper'
import { MENU_URL } from '../utils/urls'
import { Menu } from '../models/Menu'
import { MenuCreate } from '../models/MenuCreate'
import { AppDispatch, RootState } from './store'
import { menuSchema } from '../models/schema'
import { initialQuery } from '../utils/helpers/CRUDHelper'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { TableParams } from '../models/TableParams'

const initialState: SBState<Menu> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'menu',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer

export const {
  getItemsSuccess: getMenusSuccess,
  setQuery: setMenuQuery,
  resetQueryAndIds: resetMenuQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<{ [key: string]: Menu }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMenus = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectAllMenus = () =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items).map((i) => i[1])
  )
export const selectSelectedMenu = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectMenuById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectMenusByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedMenu =
  (Menu: Menu | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Menu>(Menu)))

export const getMenus = (params: TableParams) =>
  SBAPIFetchPaginatedDispatch<Menu>(
    MENU_URL,
    params,
    [menuSchema],
    slice.actions
  )

export const getMenuWithId = (id: number) =>
  SBAPIFetchDispatch<Menu>(`${MENU_URL}/${id}`, menuSchema, slice.actions)

export const createMenu = (menu: MenuCreate) =>
  SBAPICreateDispatch<MenuCreate>(menu, MENU_URL, menuSchema, slice.actions)

export const updateMenu = (menu: Menu) =>
  SBAPIUpdateDispatch<Menu>(menu, `${MENU_URL}/${menu.id}`, slice.actions)

export const deleteMenu = (menu: Menu) =>
  SBAPIDeleteDispatch<Menu>(menu, `${MENU_URL}/${menu.id}`, slice.actions)
